export interface RemoteZipFile {
  name: string;
  url: string;
  lastModified?: string | number | Date | null;
}

export interface ZipFileEntry {
  name: string;
  data: Uint8Array;
  lastModified?: string | number | Date | null;
}

const textEncoder = new TextEncoder();

let crcTable: Uint32Array | null = null;

const getCrcTable = () => {
  if (crcTable) return crcTable;
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let value = i;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[i] = value >>> 0;
  }
  crcTable = table;
  return table;
};

const crc32 = (data: Uint8Array) => {
  const table = getCrcTable();
  let crc = 0xffffffff;
  for (const byte of data) {
    crc = table[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const normalizeZipName = (name: string, index: number) => {
  const cleaned = String(name || '')
    .replace(/\\/g, '/')
    .split('/')
    .map((part) => part.trim().replace(/[\u0000-\u001f<>:"|?*]+/g, '-'))
    .filter(Boolean)
    .join('/');
  return cleaned || `ficheiro-${String(index + 1).padStart(2, '0')}`;
};

const toDate = (value: ZipFileEntry['lastModified']) => {
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.getTime()) ? new Date() : date;
};

const dosDateTime = (value: ZipFileEntry['lastModified']) => {
  const date = toDate(value);
  const year = Math.max(1980, date.getFullYear());
  const dosTime = (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
  const dosDate = ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();
  return { dosDate, dosTime };
};

const writeStringBytes = (target: Uint8Array, offset: number, value: Uint8Array) => {
  target.set(value, offset);
};

export function createZipBlob(files: ZipFileEntry[]) {
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;

  files.forEach((file, index) => {
    const nameBytes = textEncoder.encode(normalizeZipName(file.name, index));
    const data = file.data;
    const { dosDate, dosTime } = dosDateTime(file.lastModified);
    const crc = crc32(data);
    const localHeader = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(localHeader.buffer);

    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, 20, true);
    localView.setUint16(6, 0x0800, true);
    localView.setUint16(8, 0, true);
    localView.setUint16(10, dosTime, true);
    localView.setUint16(12, dosDate, true);
    localView.setUint32(14, crc, true);
    localView.setUint32(18, data.byteLength, true);
    localView.setUint32(22, data.byteLength, true);
    localView.setUint16(26, nameBytes.length, true);
    localView.setUint16(28, 0, true);
    writeStringBytes(localHeader, 30, nameBytes);

    localParts.push(localHeader, data);

    const centralHeader = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint16(8, 0x0800, true);
    centralView.setUint16(10, 0, true);
    centralView.setUint16(12, dosTime, true);
    centralView.setUint16(14, dosDate, true);
    centralView.setUint32(16, crc, true);
    centralView.setUint32(20, data.byteLength, true);
    centralView.setUint32(24, data.byteLength, true);
    centralView.setUint16(28, nameBytes.length, true);
    centralView.setUint16(30, 0, true);
    centralView.setUint16(32, 0, true);
    centralView.setUint16(34, 0, true);
    centralView.setUint16(36, 0, true);
    centralView.setUint32(38, 0, true);
    centralView.setUint32(42, offset, true);
    writeStringBytes(centralHeader, 46, nameBytes);
    centralParts.push(centralHeader);

    offset += localHeader.byteLength + data.byteLength;
  });

  const centralSize = centralParts.reduce((sum, part) => sum + part.byteLength, 0);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(8, files.length, true);
  endView.setUint16(10, files.length, true);
  endView.setUint32(12, centralSize, true);
  endView.setUint32(16, offset, true);

  return new Blob([...localParts, ...centralParts, end], { type: 'application/zip' });
}

export async function fetchRemoteFilesAsZip(
  files: RemoteZipFile[],
  onProgress?: (completed: number, total: number) => void
) {
  const entries: ZipFileEntry[] = [];
  for (const [index, file] of files.entries()) {
    const response = await fetch(file.url, { cache: 'no-store' });
    if (!response.ok) throw new Error(`Falha ao descarregar ${file.name}: ${response.status}`);
    entries.push({
      name: file.name,
      data: new Uint8Array(await response.arrayBuffer()),
      lastModified: file.lastModified
    });
    onProgress?.(index + 1, files.length);
  }
  return createZipBlob(entries);
}

export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
